import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { resolveScheduledVisitType } from '../utils/defaultSchedule';
import persistProfilePref from '../utils/persistProfilePref';

/**
 * Custom hook for managing patient/soldier form state
 * Extracted from App.js — all patient demographics, clinical options,
 * imaging, specialties, letter options, encounter info, and soldier info.
 */
export const usePatientForm = () => {
  // Identity fields ARE STICKY — Feb 24, 2026 (user fix).
  // Repeated complaint: refresh / Next Patient / regenerate wipes patient name,
  // rank, unit, phone, occupation, MTF, provider sig block. None of those
  // change per-encounter for the same user, so we persist them via
  // localStorage and rehydrate on mount. clearAll() and restoreFormData()
  // are also updated to leave these alone.
  const _ls = (k, fallback = '') => {
    try { return localStorage.getItem(k) ?? fallback; } catch { return fallback; }
  };
  const _persistEffect = (key, value) => {
    try {
      if (value === undefined || value === null || value === '') localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch { /* ignore */ }
  };

  // Patient Demographics
  const [patientAge, setPatientAge] = useState('');
  const [patientGender, setPatientGender] = useState('');
  // Feb 25, 2026 — patient_status reverted to NON-sticky per user request.
  // It was bleeding from one patient to the next (Civilian → AD etc.). It
  // still survives a same-session refresh via the legacy autosave path if
  // present, but resetPatientForm / clearAll now wipe it.
  const [patientStatus, setPatientStatus] = useState('');
  const [patientName, setPatientName] = useState(() => _ls('writeright_identity_patient_name'));
  const [chiefComplaint, setChiefComplaint] = useState('');
  useEffect(() => { _persistEffect('writeright_identity_patient_name', patientName); }, [patientName]);

  // Patient Label for No-PII identification (appointment time slots)
  const [patientLabel, setPatientLabel] = useState('');
  const [clinicSchedule, setClinicSchedule] = useState([]);

  // Track auto-filled fields for green highlighting
  const [autoFilledFields, setAutoFilledFields] = useState({ age: false, gender: false, status: false, chiefComplaint: false });

  // Imaging & Specialties
  const [imagingOrders, setImagingOrders] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [manualIcd10, setManualIcd10] = useState('');
  const [primaryIcd10Codes, setPrimaryIcd10Codes] = useState([]);
  // Mar 3, 2026 — Dismissed sets (per encounter). When the provider taps
  // × on an auto-suggested imaging or referral chip, the extractor in
  // useImagingSpecialtyExtraction would otherwise re-add it on the next
  // debounce tick (1.5s later) because the same trigger word still lives
  // in `notes`. Store what the provider explicitly dismissed and honor
  // that during extraction until the next Next-Patient reset.
  //
  // Keys:
  //   specialties → lowercase specialty name string ("orthopedic surgery")
  //   imaging     → "type|bodyPart" lowercase composite key
  const [dismissedSpecialties, setDismissedSpecialties] = useState(() => new Set());
  const [dismissedImaging, setDismissedImaging] = useState(() => new Set());

  // Letter Options
  const [charLimit, setCharLimit] = useState('300');
  const [letterType, setLetterType] = useState('');
  const [letterContext, setLetterContext] = useState('');
  const [includeCoverLetter, setIncludeCoverLetter] = useState(false);

  // Enhanced letter options for provider mode
  const [customLetterDescription, setCustomLetterDescription] = useState('');
  const [providerLetterTone, setProviderLetterTone] = useState('professional');
  const [includePatientQuotes, setIncludePatientQuotes] = useState('none');
  const [clinicalTone, setClinicalTone] = useState(() => localStorage.getItem('writeright_clinical_tone') || 'educational');
  useEffect(() => {
    try { localStorage.setItem('writeright_clinical_tone', clinicalTone); } catch { /* ignore */ }
  }, [clinicalTone]);

  // Resolve scheduled visit type once (shared default for visitType + PE toggles)
  // Uses shared resolver that seeds localStorage with DEFAULT_VISIT_SCHEDULE if
  // the user has never saved Profile Settings (fixes "Thursday PM defaults to
  // in-person" bug reported May 5, 2026).
  const _scheduledVisitType = resolveScheduledVisitType();
  const _isVirtualScheduled = _scheduledVisitType === 'virtual' || _scheduledVisitType === 'results_review_virtual';
  const _isInPersonScheduled = _scheduledVisitType === 'in_person_exam' || _scheduledVisitType === 'results_review';
  // Override: if provider has flipped "Always Remarkable PE" in Profile Settings,
  // ignore schedule entirely — PE on + Remarkable regardless.
  const _peAlwaysRemarkable = (() => {
    try { return localStorage.getItem('writeright_pe_always_remarkable') === 'true'; }
    catch { return false; }
  })();

  // Note section toggles — schedule-aware defaults:
  // • Virtual visits → PE off (no physical exam in telehealth)
  // • In-person visits → PE on
  // • PE default is now ALWAYS-ON + UNREMARKABLE (user pref, Feb 27, 2026 v6.8.14):
  //   "PE has to always be filled out — it's just a matter of whether it's
  //    remarkable or unremarkable." So both flags are forced true and the
  //    OutputsHeaderRow exposes only the "Except for…" sub-control.
  // • ROS is likewise always-on (provider: "when would you NOT use ROS?
  //   that's the whole HPI combination, so ROS is automatic").
  const [includeROS, setIncludeROS] = useState(true);
  const [includePE, setIncludePE] = useState(true);
  const [peUnremarkable, setPeUnremarkable] = useState(true);
  // Mar 2, 2026 — Hydrate peExceptions from localStorage so it survives
  // accidental refreshes, back-navigation, and Emergent-chat blur events
  // (user report: "PE and indication are not auto saving when you click
  // out the box"). Cleared explicitly on Next Patient / clearAll.
  const [peExceptions, setPeExceptions] = useState(() => {
    try { return localStorage.getItem('writeright_pe_exceptions') || ''; } catch (_) { return ''; }
  });
  useEffect(() => {
    try {
      if (peExceptions) localStorage.setItem('writeright_pe_exceptions', peExceptions);
      else localStorage.removeItem('writeright_pe_exceptions');
    } catch (_) { /* ignore */ }
  }, [peExceptions]);

  // Mar 2, 2026 — Auto-clear peExceptions when Chief Complaint pivots to a
  // clearly different visit. Guards against stale PE findings bleeding into
  // the next patient when the provider forgets to hit "Next Patient".
  // Effect body is declared later once chiefComplaint is in scope.
  const prevChiefComplaintRef = useRef('');
  // Effect body is declared later once all encounter state is in scope.
  // Medic Mode (Feb 24, 2026, revised Feb 26, 2026):
  //  - Per-encounter toggle on the input card.
  //  - Profile setting `default_medic_mode` decides the STARTING value for
  //    new encounters. If you flip the per-encounter toggle, that overrides
  //    just for this note. "Next Patient" / clearAll resets back to the
  //    profile default.
  //  - Falls back to `false` if not logged in or profile field missing.
  const [medicMode, setMedicMode] = useState(false);
  // Cleanup legacy sticky key (Feb 25, 2026).
  useEffect(() => {
    try { localStorage.removeItem('writeright_medic_mode'); } catch { /* ignore */ }
  }, []);

  // Prior-visit context — Feb 25, 2026. Free-text encounter-scoped box for
  // "At Your Last Visit" content (prior A/P summary, education goals,
  // barriers, social-hx that shapes today's EQ). Cleared on Next Patient.
  const [priorVisitContext, setPriorVisitContext] = useState('');
  // Pre-Visit Prep / RN Handoff buffer (Feb 26, 2026). Separate textarea
  // surfaced as a small expandable card; populated by RN report dictation
  // and provider's pre-room thinking-aloud. Sent to backend as `prep_notes`.
  const [prepNotes, setPrepNotes] = useState('');

  // Encounter info
  const [encounterType, setEncounterType] = useState('established');
  // Mar 1, 2026 — Initialize visitType from saved `last_visit_type` profile pref
  // if available (provider's habitual workflow). Falls back to today's scheduled
  // visit type otherwise. Profile hydration is async; `useProfile.js` writes
  // `writeright_last_visit_type` into localStorage on user-data load, so by
  // the time this state initializer runs after sign-in it has the value.
  const [visitType, setVisitType] = useState(() => {
    try {
      const stored = localStorage.getItem('writeright_last_visit_type');
      if (stored) return stored;
    } catch (_) { /* ignore */ }
    return _scheduledVisitType;
  });
  // Mar 1, 2026 — Auto-persist visit type to profile + localStorage every
  // change. Debounced inside `persistProfilePref` so rapid pill-flips coalesce.
  useEffect(() => {
    if (!visitType) return;
    try { localStorage.setItem('writeright_last_visit_type', visitType); }
    catch (_) { /* ignore */ }
    persistProfilePref({ last_visit_type: visitType });
  }, [visitType]);
  const [panelOwnership, setPanelOwnership] = useState('my_patient');
  const [appointmentType, setAppointmentType] = useState('');

  // Soldier/Service Member Info — STICKY (Feb 24, 2026 fix)
  const [soldierRank, setSoldierRank] = useState(() => _ls('writeright_identity_rank'));
  const [soldierFirstName, setSoldierFirstName] = useState(() => _ls('writeright_identity_first_name'));
  const [soldierLastName, setSoldierLastName] = useState(() => _ls('writeright_identity_last_name'));
  const [soldierDob, setSoldierDob] = useState(() => _ls('writeright_identity_dob'));
  const [soldierUnit, setSoldierUnit] = useState(() => _ls('writeright_identity_unit'));
  const [soldierPhone, setSoldierPhone] = useState(() => _ls('writeright_identity_phone'));
  const [mtfName, setMtfName] = useState(() => _ls('writeright_identity_mtf') || 'Moncrief Army Health Clinic, Fort Jackson, SC');
  const [providerSignatureBlock, setProviderSignatureBlock] = useState(() => _ls('writeright_identity_sig_block'));
  const [customMtf, setCustomMtf] = useState(() => _ls('writeright_identity_custom_mtf'));

  // Persist identity to localStorage on any change so refresh / Next Patient
  // / regenerate don't wipe them.
  useEffect(() => { _persistEffect('writeright_identity_rank', soldierRank); }, [soldierRank]);
  useEffect(() => { _persistEffect('writeright_identity_first_name', soldierFirstName); }, [soldierFirstName]);
  useEffect(() => { _persistEffect('writeright_identity_last_name', soldierLastName); }, [soldierLastName]);
  useEffect(() => { _persistEffect('writeright_identity_dob', soldierDob); }, [soldierDob]);
  useEffect(() => { _persistEffect('writeright_identity_unit', soldierUnit); }, [soldierUnit]);
  useEffect(() => { _persistEffect('writeright_identity_phone', soldierPhone); }, [soldierPhone]);
  useEffect(() => { _persistEffect('writeright_identity_mtf', mtfName); }, [mtfName]);
  useEffect(() => { _persistEffect('writeright_identity_sig_block', providerSignatureBlock); }, [providerSignatureBlock]);
  useEffect(() => { _persistEffect('writeright_identity_custom_mtf', customMtf); }, [customMtf]);

  // Occupation / Job info — also sticky (rarely changes per-encounter)
  const [occupation, setOccupation] = useState(() => _ls('writeright_identity_occupation'));
  const [jobDuties, setJobDuties] = useState(() => _ls('writeright_identity_job_duties'));
  useEffect(() => { _persistEffect('writeright_identity_occupation', occupation); }, [occupation]);
  useEffect(() => { _persistEffect('writeright_identity_job_duties', jobDuties); }, [jobDuties]);

  // Generate toggles
  const [generateIndication, setGenerateIndication] = useState(true);
  const [generateClinicalNote, setGenerateClinicalNote] = useState(true);
  const [generateAdminLetter, setGenerateAdminLetter] = useState(false);
  const [generateLimitations, setGenerateLimitations] = useState(false);
  const [includeEmAnalysis, setIncludeEmAnalysis] = useState(false);
  const [apFormat, setApFormat] = useState(() => localStorage.getItem('writeright_ap_format') || 'bulleted');
  useEffect(() => {
    try { localStorage.setItem('writeright_ap_format', apFormat); } catch { /* ignore */ }
  }, [apFormat]);

  // Letter Style Options (for patient mode)
  const [letterTone, setLetterTone] = useState('professional');
  const [letterPageLimit, setLetterPageLimit] = useState('none');
  const [letterFormat, setLetterFormat] = useState('plain');
  const [referencePosition, setReferencePosition] = useState('none');

  // Document Mode (patient wizard step 3)
  const [documentMode, setDocumentMode] = useState('letter'); // 'letter', 'message', 'both'
  const [messageScenario, setMessageScenario] = useState('');

  // Consult-specific fields
  const [consultRecipient, setConsultRecipient] = useState('');
  const [consultQuestion, setConsultQuestion] = useState('');

  // Profile Restrictions Helper
  const [selectedRestriction, setSelectedRestriction] = useState('');
  const [showRestrictionsHelper, setShowRestrictionsHelper] = useState(false);

  // Next Visit / Deferred Topics (Z71.89)
  const [deferredTopics, setDeferredTopics] = useState('');

  // Specialty handlers
  const addSpecialty = useCallback(() => setSpecialties(prev => [...prev, '']), []);
  // Mar 3, 2026 — Legacy "keep at least 1 row" guard removed. In the old
  // bulky-row UI we needed a phantom empty row for the add-then-fill flow.
  // The chip UI (OrderChipRow) filters out empty rows already, so blocking
  // removal of the last chip just made the × button silently no-op.
  const removeSpecialty = useCallback((index) => {
    setSpecialties(prev => {
      const removed = prev[index];
      // Extract the "core" specialty (before " || indication") and remember
      // that the provider dismissed it, so the auto-extractor doesn't
      // re-inject it on the next debounce.
      if (typeof removed === 'string' && removed) {
        const core = removed.split('||')[0].trim().toLowerCase();
        if (core) {
          setDismissedSpecialties((d) => {
            const next = new Set(d);
            next.add(core);
            return next;
          });
        }
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);
  const updateSpecialty = useCallback((index, value) => {
    // Provider explicitly picked a specialty — undismiss it if it was
    // previously dismissed, so the extractor can auto-fill it again in
    // future edits/regens.
    if (typeof value === 'string' && value) {
      const core = value.split('||')[0].trim().toLowerCase();
      if (core) {
        setDismissedSpecialties((d) => {
          if (!d.has(core)) return d;
          const next = new Set(d);
          next.delete(core);
          return next;
        });
      }
    }
    setSpecialties(prev => { const updated = [...prev]; updated[index] = value; return updated; });
  }, []);
  // Atomically append a fully-formed specialty value. Used by the Undo
  // toast in ImagingReferralsInput — avoids the addSpecialty + timeout +
  // updateSpecialty(newIdx) race that leaks stale `specialties.length`.
  const restoreSpecialty = useCallback((value) => {
    if (typeof value === 'string' && value) {
      const core = value.split('||')[0].trim().toLowerCase();
      if (core) {
        setDismissedSpecialties((d) => {
          if (!d.has(core)) return d;
          const next = new Set(d);
          next.delete(core);
          return next;
        });
      }
    }
    setSpecialties(prev => [...prev, value]);
  }, []);

  // Imaging management
  const addImaging = useCallback(() => setImagingOrders(prev => [...prev, { type: '', bodyPart: '', indication: '' }]), []);
  // Mar 3, 2026 — Same fix as removeSpecialty above. Chip UI filters blanks
  // in its `items` list, so a truly empty array is fine and lets the × work.
  const removeImaging = useCallback((index) => {
    setImagingOrders(prev => {
      const removed = prev[index];
      if (removed && (removed.type || removed.bodyPart)) {
        const key = `${(removed.type || '').toLowerCase()}|${(removed.bodyPart || '').toLowerCase()}`;
        setDismissedImaging((d) => {
          const next = new Set(d);
          next.add(key);
          return next;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);
  // Atomically append a fully-formed imaging entry (Undo toast helper).
  const restoreImaging = useCallback((entry) => {
    if (entry && (entry.type || entry.bodyPart)) {
      const key = `${(entry.type || '').toLowerCase()}|${(entry.bodyPart || '').toLowerCase()}`;
      setDismissedImaging((d) => {
        if (!d.has(key)) return d;
        const next = new Set(d);
        next.delete(key);
        return next;
      });
    }
    setImagingOrders(prev => [...prev, {
      type: entry?.type || '',
      bodyPart: entry?.bodyPart || '',
      indication: entry?.indication || '',
    }]);
  }, []);
  const updateImagingType = useCallback((index, value) => {
    setImagingOrders(prev => { const updated = [...prev]; updated[index] = { ...updated[index], type: value }; return updated; });
  }, []);
  const updateImagingBodyPart = useCallback((index, value) => {
    setImagingOrders(prev => { const updated = [...prev]; updated[index] = { ...updated[index], bodyPart: value }; return updated; });
  }, []);
  // Feb 27, 2026 — Provider-supplied imaging indication (free-text). When
  // present, the backend uses this as the PRIMARY focus of the imaging
  // indication body instead of inferring from the notes. Genesis hard cap
  // is ~400 chars; UI surfaces amber@360 / red@390 to warn before overflow.
  const updateImagingIndication = useCallback((index, value) => {
    setImagingOrders(prev => { const updated = [...prev]; updated[index] = { ...updated[index], indication: value }; return updated; });
  }, []);

  // Reset all patient form state
  // NOTE (Feb 24, 2026): Identity fields (patientName, soldierFirstName/...,
  // mtfName, occupation, jobDuties, etc.) are NO LONGER cleared here. They
  // persist via localStorage so refresh / Next Patient / regen don't force
  // the user to retype the same identity info every time.

  // Mar 2, 2026 — CC-pivot auto-clear for ALL encounter-scoped clinical
  // fields (not just peExceptions). Per user: "of course I want that, I
  // don't want old bleeding to next pt". Guards every clinical detail
  // that could ride into a new visit if the provider forgets to hit
  // "Next Patient". Undoable via a single toast action that restores the
  // full snapshot. Identity/schedule/profile settings are excluded — those
  // are person-level, not visit-level.
  useEffect(() => {
    const norm = (s) => (s || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 2 && !['the','and','for','with','has','was','now','who','she','him','her','from','this','that','since'].includes(w));
    const prev = prevChiefComplaintRef.current;
    const curr = chiefComplaint || '';
    prevChiefComplaintRef.current = curr;
    if (!prev.trim() || !curr.trim()) return; // ignore empty→typing transitions

    // Snapshot every encounter-scoped field so we can offer a full Undo
    const snapshot = {
      peExceptions,
      deferredTopics,
      priorVisitContext,
      prepNotes,
      imagingOrders,
      specialties,
      manualIcd10,
      primaryIcd10Codes,
      letterContext,
      messageScenario,
      consultRecipient,
      consultQuestion,
      selectedRestriction,
    };
    const hasAnyStaleContent =
      (snapshot.peExceptions || '').trim() ||
      (snapshot.deferredTopics || '').trim() ||
      (snapshot.priorVisitContext || '').trim() ||
      (snapshot.prepNotes || '').trim() ||
      (Array.isArray(snapshot.imagingOrders) && snapshot.imagingOrders.some((o) => o?.type || o?.bodyPart || o?.indication)) ||
      (Array.isArray(snapshot.specialties) && snapshot.specialties.some((s) => s && s.trim())) ||
      (snapshot.manualIcd10 || '').trim() ||
      (Array.isArray(snapshot.primaryIcd10Codes) && snapshot.primaryIcd10Codes.length > 0) ||
      (snapshot.letterContext || '').trim() ||
      (snapshot.messageScenario || '').trim() ||
      (snapshot.consultRecipient || '').trim() ||
      (snapshot.consultQuestion || '').trim() ||
      (snapshot.selectedRestriction || '').trim();
    if (!hasAnyStaleContent) return;

    const wa = new Set(norm(prev));
    const wb = new Set(norm(curr));
    if (wa.size === 0 || wb.size === 0) return;
    let shared = 0;
    wa.forEach((w) => { if (wb.has(w)) shared += 1; });
    const overlap = shared / Math.max(wa.size, wb.size);
    if (overlap >= 0.4) return; // near-identical edit, keep everything

    // CC pivoted substantially — clear every encounter-scoped field.
    setPeExceptions('');
    try { localStorage.removeItem('writeright_pe_exceptions'); } catch (_) { /* ignore */ }
    setDeferredTopics('');
    setPriorVisitContext('');
    setPrepNotes('');
    setImagingOrders([{ type: '', bodyPart: '', indication: '' }]);
    setSpecialties(['']);
    // Mar 3, 2026 — Clear the dismissed sets too. The prior encounter's
    // dismissals shouldn't leak into the new patient (different notes
    // → different auto-extractions → different candidates).
    setDismissedSpecialties(new Set());
    setDismissedImaging(new Set());
    setManualIcd10('');
    setPrimaryIcd10Codes([]);
    setLetterContext('');
    setMessageScenario('');
    setConsultRecipient('');
    setConsultQuestion('');
    setSelectedRestriction('');

    // Summarize what was wiped so the provider knows the scope
    const wiped = [];
    if (snapshot.peExceptions?.trim()) wiped.push('PE findings');
    if (snapshot.deferredTopics?.trim()) wiped.push('deferred topics');
    if (snapshot.priorVisitContext?.trim()) wiped.push('prior visit');
    if (snapshot.prepNotes?.trim()) wiped.push('prep notes');
    if (snapshot.imagingOrders?.some((o) => o?.type)) wiped.push('imaging orders');
    if (snapshot.specialties?.some((s) => s && s.trim())) wiped.push('referrals');
    if (snapshot.manualIcd10?.trim() || snapshot.primaryIcd10Codes?.length) wiped.push('ICD-10');
    if (snapshot.letterContext?.trim()) wiped.push('letter context');
    if (snapshot.messageScenario?.trim()) wiped.push('message scenario');
    if (snapshot.consultRecipient?.trim() || snapshot.consultQuestion?.trim()) wiped.push('consult');
    if (snapshot.selectedRestriction?.trim()) wiped.push('restriction');

    toast('Cleared stale visit details for the new complaint', {
      description: `Wiped: ${wiped.join(', ')}. New CC: "${curr.slice(0, 60)}${curr.length > 60 ? '…' : ''}"`,
      action: {
        label: 'Undo',
        onClick: () => {
          setPeExceptions(snapshot.peExceptions);
          setDeferredTopics(snapshot.deferredTopics);
          setPriorVisitContext(snapshot.priorVisitContext);
          setPrepNotes(snapshot.prepNotes);
          setImagingOrders(snapshot.imagingOrders);
          setSpecialties(snapshot.specialties);
          setManualIcd10(snapshot.manualIcd10);
          setPrimaryIcd10Codes(snapshot.primaryIcd10Codes);
          setLetterContext(snapshot.letterContext);
          setMessageScenario(snapshot.messageScenario);
          setConsultRecipient(snapshot.consultRecipient);
          setConsultQuestion(snapshot.consultQuestion);
          setSelectedRestriction(snapshot.selectedRestriction);
        },
      },
      duration: 8000,
    });
  }, [chiefComplaint, peExceptions, deferredTopics, priorVisitContext, prepNotes, imagingOrders, specialties, manualIcd10, primaryIcd10Codes, letterContext, messageScenario, consultRecipient, consultQuestion, selectedRestriction]);

  const resetPatientForm = useCallback(() => {
    setPatientAge(''); setPatientGender(''); setChiefComplaint('');
    setManualIcd10(''); setPrimaryIcd10Codes([]); setPatientLabel('');
    setLetterType(''); setLetterContext(''); setDeferredTopics('');
    setDocumentMode('letter'); setMessageScenario('');
    setAutoFilledFields({ age: false, gender: false, status: false, chiefComplaint: false });
    setIncludeCoverLetter(false);
    setImagingOrders([{ type: '', bodyPart: '', indication: '' }]); setSpecialties(['']);
    // Mar 3, 2026 — Also clear the dismissed sets on Next Patient so
    // auto-extraction gets a clean slate for the new encounter.
    setDismissedSpecialties(new Set());
    setDismissedImaging(new Set());
    // Mar 2, 2026 — Clear persisted peExceptions on Next Patient so the next
    // encounter starts clean. (peExceptions itself is persisted to
    // localStorage while the current encounter is active.)
    setPeExceptions('');
    try { localStorage.removeItem('writeright_pe_exceptions'); } catch (_) { /* ignore */ }
  }, []);

  return {
    // Patient Demographics
    patientAge, setPatientAge, patientGender, setPatientGender,
    patientStatus, setPatientStatus, patientName, setPatientName,
    chiefComplaint, setChiefComplaint,
    patientLabel, setPatientLabel, clinicSchedule, setClinicSchedule,
    autoFilledFields, setAutoFilledFields,

    // Imaging & Specialties
    imagingOrders, setImagingOrders, specialties, setSpecialties,
    manualIcd10, setManualIcd10, primaryIcd10Codes, setPrimaryIcd10Codes,
    addSpecialty, removeSpecialty, updateSpecialty, restoreSpecialty,
    addImaging, removeImaging, updateImagingType, updateImagingBodyPart, updateImagingIndication, restoreImaging,
    // Mar 3, 2026 — Dismissed sets for auto-extraction filtering.
    dismissedSpecialties, dismissedImaging,

    // Letter Options
    charLimit, setCharLimit, letterType, setLetterType,
    letterContext, setLetterContext, includeCoverLetter, setIncludeCoverLetter,
    customLetterDescription, setCustomLetterDescription,
    providerLetterTone, setProviderLetterTone,
    includePatientQuotes, setIncludePatientQuotes,
    clinicalTone, setClinicalTone,

    // Clinical toggles
    includeROS, setIncludeROS, includePE, setIncludePE,
    peUnremarkable, setPeUnremarkable,
    peExceptions, setPeExceptions,
    medicMode, setMedicMode,
    priorVisitContext, setPriorVisitContext,
    prepNotes, setPrepNotes,

    // Encounter info
    encounterType, setEncounterType, visitType, setVisitType,
    panelOwnership, setPanelOwnership, appointmentType, setAppointmentType,

    // Soldier Info
    soldierRank, setSoldierRank, soldierFirstName, setSoldierFirstName,
    soldierLastName, setSoldierLastName, soldierDob, setSoldierDob,
    soldierUnit, setSoldierUnit, soldierPhone, setSoldierPhone,
    mtfName, setMtfName, providerSignatureBlock, setProviderSignatureBlock,
    customMtf, setCustomMtf,

    // Occupation
    occupation, setOccupation, jobDuties, setJobDuties,

    // Generate toggles
    generateIndication, setGenerateIndication, generateClinicalNote, setGenerateClinicalNote,
    generateAdminLetter, setGenerateAdminLetter, generateLimitations, setGenerateLimitations,
    includeEmAnalysis, setIncludeEmAnalysis,
    apFormat, setApFormat,

    // Letter style
    letterTone, setLetterTone, letterPageLimit, setLetterPageLimit,
    letterFormat, setLetterFormat, referencePosition, setReferencePosition,

    // Document mode (letter / message / both)
    documentMode, setDocumentMode, messageScenario, setMessageScenario,

    // Consult
    consultRecipient, setConsultRecipient, consultQuestion, setConsultQuestion,

    // Restrictions
    selectedRestriction, setSelectedRestriction,
    showRestrictionsHelper, setShowRestrictionsHelper,

    // Deferred / Next Visit Topics
    deferredTopics, setDeferredTopics,

    // Actions
    resetPatientForm,
  };
};

export default usePatientForm;
